import type { InquiryTemplateSettings } from "@/features/material-compliance/components/supplier-declarations/inquiry/settings/templateSettingsMock"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  clampNumber,
  SettingsNumberInput,
  SettingsSwitchField,
} from "../../../components/SettingsControls"
import {
  SettingsField,
  SettingsSection,
} from "../../../components/SettingsPage"
import { SettingsWithPreview } from "../../../components/SettingsPreview"
import { TemplateSaveButton } from "../../../components/TemplateSaveButton"
import { TemplateSettingsLoader } from "../../../components/TemplateSettingsLoader"
import { useSavedDraft } from "../../../hooks/useSavedDraft"
import {
  useTemplateSettingsSave,
  type TemplateSettingsSaver,
} from "../../../hooks/useTemplateSettingsSave"
import { CadenceTimelinePreview } from "../../../previews/CadenceTimelinePreview"

// The ranges the backend accepts.
const DUE_DAYS = { min: 1, max: 365 }
const INTERVAL_DAYS = { min: 1, max: 90 }
const MAX_REMINDERS = { min: 1, max: 15 }

function DeadlinesForm({
  saved,
  saver,
}: {
  saved: InquiryTemplateSettings
  saver: TemplateSettingsSaver
}) {
  const { translate } = useTranslation()
  const [dueDays, setDueDays] = useSavedDraft(saved.defaultDueDays)
  const [remindersEnabled, setRemindersEnabled] = useSavedDraft(
    saved.remindersEnabled,
  )
  const [intervalDays, setIntervalDays] = useSavedDraft(
    saved.reminderIntervalDays,
  )
  const [maxReminders, setMaxReminders] = useSavedDraft(saved.maxReminders)
  const readOnly = !saver.canEdit
  const days = translate("SETTINGS/UNIT_DAYS")

  const cadence = {
    dueDays: clampNumber(dueDays, DUE_DAYS.min, DUE_DAYS.max),
    remindersEnabled,
    intervalDays: clampNumber(
      intervalDays,
      INTERVAL_DAYS.min,
      INTERVAL_DAYS.max,
    ),
    maxReminders: clampNumber(
      maxReminders,
      MAX_REMINDERS.min,
      MAX_REMINDERS.max,
    ),
  }

  return (
    <SettingsWithPreview preview={<CadenceTimelinePreview cadence={cadence} />}>
      <div className="flex flex-col divide-y">
        <SettingsSection
          title={translate("MC_TPL_CADENCE_DUE_LABEL")}
          target="inquiry.deadline"
          info={translate("SETTINGS/INFO/NEW_ONLY")}
          description={translate("MC_TPL_CADENCE_DUE_HINT")}
          footer={
            <TemplateSaveButton
              saver={saver}
              saveKey="deadline"
              dirty={cadence.dueDays !== saved.defaultDueDays}
              patch={() => ({ defaultDueDays: cadence.dueDays })}
            />
          }
        >
          <SettingsNumberInput
            id="settings-due-days"
            label={translate("MC_TPL_CADENCE_DUE_LABEL")}
            value={dueDays}
            {...DUE_DAYS}
            unit={days}
            disabled={readOnly}
            onChange={setDueDays}
          />
        </SettingsSection>

        <SettingsSection
          title={translate("MC_TPL_CADENCE_HEADING")}
          target="inquiry.reminders"
          info={translate("SETTINGS/INFO/ALSO_RUNNING")}
          footer={
            <TemplateSaveButton
              saver={saver}
              saveKey="reminders"
              dirty={
                remindersEnabled !== saved.remindersEnabled ||
                cadence.intervalDays !== saved.reminderIntervalDays ||
                cadence.maxReminders !== saved.maxReminders
              }
              patch={() => ({
                remindersEnabled,
                reminderIntervalDays: cadence.intervalDays,
                maxReminders: cadence.maxReminders,
              })}
            />
          }
        >
          <SettingsSwitchField
            id="settings-reminders-enabled"
            label={translate("MC_TPL_CADENCE_ENABLED_LABEL")}
            description={translate("MC_TPL_CADENCE_ENABLED_HINT")}
            checked={remindersEnabled}
            disabled={readOnly}
            onCheckedChange={setRemindersEnabled}
          />
          <SettingsField
            htmlFor="settings-reminder-interval"
            label={translate("MC_TPL_CADENCE_INTERVAL_LABEL")}
            hint={translate("MC_TPL_CADENCE_INTERVAL_HINT")}
          >
            <SettingsNumberInput
              id="settings-reminder-interval"
              value={intervalDays}
              {...INTERVAL_DAYS}
              unit={days}
              disabled={readOnly || !remindersEnabled}
              onChange={setIntervalDays}
            />
          </SettingsField>
          <SettingsField
            htmlFor="settings-max-reminders"
            label={translate("MC_TPL_CADENCE_MAX_LABEL")}
            hint={translate("MC_TPL_CADENCE_MAX_HINT")}
          >
            <SettingsNumberInput
              id="settings-max-reminders"
              value={maxReminders}
              {...MAX_REMINDERS}
              unit={translate("MC_TPL_CADENCE_REMINDERS_UNIT")}
              disabled={readOnly || !remindersEnabled}
              onChange={setMaxReminders}
            />
          </SettingsField>
        </SettingsSection>
      </div>
    </SettingsWithPreview>
  )
}

/** When a supplier's answer is due, and how they are reminded. */
export function DeadlinesTab({ saver }: { saver: TemplateSettingsSaver }) {
  return (
    <TemplateSettingsLoader>
      {(settings) => <DeadlinesForm saved={settings} saver={saver} />}
    </TemplateSettingsLoader>
  )
}

/** The deadline and reminders with their own Save: for the inquiry draft's
 *  side sheet. */
export function DeadlinesSettings() {
  const saver = useTemplateSettingsSave()
  return <DeadlinesTab saver={saver} />
}
